import React from 'react';
import PropTypes from 'prop-types';
import { Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import DistributionCentreRow from './DistributionCentreRow';
import './DistributionCentreSection.css';

/**
 * Distribution Centre Section Component
 *
 * Manages the entire distribution centre section with multiple rows.
 * Handles add/remove functionality, validation, and state management.
 *
 * @param {Array} fields - useFieldArray fields from React Hook Form
 * @param {Function} register - React Hook Form register function
 * @param {Object} errors - Form validation errors object
 * @param {Function} watch - React Hook Form watch function
 * @param {Function} onCountryNameChange - Handler for country selection change
 * @param {Function} onRemoveCentre - Handler for removing distribution centre
 * @param {Function} validateAccount - Validation function for fields
 * @param {boolean} canRemove - Whether centres can be removed
 * @param {number} currentCount - Current number of distribution centres
 * @param {number} maxCount - Maximum allowed distribution centres
 */
function DistributionCentreSection({
  fields,
  register,
  errors,
  watch,
  onCountryNameChange,
  onRemoveCentre,
  validateAccount,
  canRemove,
  currentCount,
  currencyOptions,
  onCurrencyChange,
  brandOptions,
  subBrandOptions,
  onBrandChange,
  onSubBrandChange,
  subBrandAccess,
}) {
  const { t } = useTranslation('common');

  const handleRemoveCentre = (index) => {
    if (onRemoveCentre && canRemove) {
      onRemoveCentre(index);
    }
  };

  const getRowKey = (field, index) => {
    return field.id || `distribution-centre-${index}`;
  };

  return fields.map((field, index) => {
    const hasRowErrors = errors?.distributionCentres?.[index] && Object.keys(errors.distributionCentres[index]).length > 0;
    const rowContainerClass = `distribution-centre-row-container mb-3 ${hasRowErrors ? 'has-errors' : ''}`;

    return (
      <div key={getRowKey(field, index)}>
        <Row className="distribution-centre-grid" data-row-index={index}>
          <DistributionCentreRow
            index={index}
            register={register}
            errors={errors}
            watch={watch}
            onCountryNameChange={onCountryNameChange}
            canRemove={canRemove && currentCount > 1}
            onRemove={handleRemoveCentre}
            validateAccount={validateAccount}
            currencyOptions={currencyOptions}
            onCurrencyChange={onCurrencyChange}
            brandOptions={brandOptions}
            subBrandOptions={subBrandOptions}
            onBrandChange={onBrandChange}
            onSubBrandChange={onSubBrandChange}
            subBrandAccess={subBrandAccess}
          />
        </Row>
      </div>
    );
  });
}

DistributionCentreSection.propTypes = {
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
    }),
  ).isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.shape({
    distributionCentres: PropTypes.arrayOf(
      PropTypes.shape({
        distributionName: PropTypes.shape({ message: PropTypes.string }),
        buildingNumber: PropTypes.shape({ message: PropTypes.string }),
        streetAddress: PropTypes.shape({ message: PropTypes.string }),
        city: PropTypes.shape({ message: PropTypes.string }),
        countryName: PropTypes.shape({ message: PropTypes.string }),
        postalCode: PropTypes.shape({ message: PropTypes.string }),
      }),
    ),
  }),
  watch: PropTypes.func.isRequired,
  onCountryNameChange: PropTypes.func.isRequired,
  onRemoveCentre: PropTypes.func.isRequired,
  validateAccount: PropTypes.func.isRequired,
  canRemove: PropTypes.bool.isRequired,
  currentCount: PropTypes.number.isRequired,
  maxCount: PropTypes.number,
  currencyOptions: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string, value: PropTypes.string })),
  onCurrencyChange: PropTypes.func.isRequired,
  brandOptions: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string, value: PropTypes.string })),
  subBrandOptions: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string, value: PropTypes.string, brandId: PropTypes.string })),
  onBrandChange: PropTypes.func.isRequired,
  onSubBrandChange: PropTypes.func.isRequired,
  subBrandAccess: PropTypes.bool,
};

DistributionCentreSection.defaultProps = {
  errors: {},
  maxCount: 20,
  currencyOptions: [],
  brandOptions: [],
  subBrandOptions: [],
  subBrandAccess: false,
};

export default DistributionCentreSection;
